import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import aiExample from '~/assets/images/x-ray/hover-ai-example.png';
import userExample from '~/assets/images/x-ray/hover-user-example.png';
import { CardSelect } from '~/eds/src/components/CardSelect/CardSelect';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Image } from '~/eds/src/components/Image/Image';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { Option } from '~/eds/src/types/Option';
import { slices } from '~/redux/slices';
import { State as XRayState } from '~/redux/slices/x-ray/types';

import { DropdownOptionsSelect } from './DropdownOptionsSelect';
import { NotApplicableOptionSelect } from './NotApplicableOptionSelect';

interface Prop {
  config: XRayState['configuration'];
  readOnly: boolean;
  readOnlyTooltip?: string;
}

export const ConfigureDropdownOptions = ({
  config,
  readOnly,
  readOnlyTooltip,
}: Prop) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const initialOptions: Option<string>[] = useMemo(
    () => config.fieldClassification?.options ?? [],
    [],
  );
  const [availableOptions, setAvailableOptions] = useState(initialOptions);
  const [selectedOptions, setSelectedOptions] = useState(initialOptions);

  // Default to close-ended if not specified in config.
  const isCloseEnded = !config.fieldClassification?.isOpenEnded;
  const disableOptionsSelect = !isCloseEnded;

  const configureDropdownOptions = [
    {
      disabled: readOnly,
      value: true,
      label: intl.formatMessage({
        id: 'XRayConfiguration.dropdownOptions.createOwnLabel',
        defaultMessage: 'Create your own options',
        description: 'Label for creating custom, close-ended dropdown options.',
      }),
      content: (
        <Layout preset="form-fields" pl={6} w="100%">
          <FormField<Option<string>, true>
            description={intl.formatMessage({
              id: 'XRayConfiguration.dropdownOptions.createOwnDescription',
              defaultMessage: 'Organize data into options you create.',
              description:
                'Description for creating custom, close-ended dropdown options.',
            })}
            disabled={disableOptionsSelect}
            input={DropdownOptionsSelect}
            inputProps={{
              options: availableOptions,
              onAddOption: (newOption: Option<string>) => {
                const updatedSelectedOptions = [...selectedOptions, newOption];
                setAvailableOptions([...availableOptions, newOption]);
                setSelectedOptions(updatedSelectedOptions);
                dispatch(
                  slices.xRay.actions.setOptions(updatedSelectedOptions),
                );
              },
            }}
            label=""
            name="dropdown-options-select-form-field"
            value={selectedOptions}
            onChange={(newOptions) => {
              if (newOptions) {
                setSelectedOptions(newOptions);
                dispatch(slices.xRay.actions.setOptions(newOptions));
              }
            }}
          />
          <FormField<Option<string>, false>
            description={intl.formatMessage({
              id: 'XRayConfiguration.dropdownOptions.createOwnDefaultOption',
              defaultMessage: 'If none of the options above apply:',
              description:
                'Description for specifying a default option when creating custom, close-ended dropdown options.',
            })}
            disabled={disableOptionsSelect}
            input={NotApplicableOptionSelect}
            label=""
            name="not-applicable-dropdown-option-select-form-field"
            value={config.fieldClassification?.defaultOption ?? null}
            onChange={(newOption) => {
              dispatch(slices.xRay.actions.setDefaultOption(newOption));
            }}
          />
        </Layout>
      ),
    },
    {
      disabled: readOnly,
      value: false,
      label: intl.formatMessage({
        id: 'XRayConfiguration.dropdownOptions.createWithAiLabel',
        defaultMessage: 'Create with AI',
        description:
          'Label for using AI to create open-ended dropdown options.',
      }),
      description: intl.formatMessage({
        id: 'XRayConfiguration.dropdownOptions.createWithAiDescription',
        defaultMessage:
          'Use AI to find values to populate your dropdown field.',
        description:
          'Description for using AI to create open-ended dropdown options.',
      }),
    },
  ];

  return (
    <FormField<boolean, false>
      isCollapsible
      example={{
        content: (
          <Layout preset="subsections">
            <Text variant="body-medium">
              <FormattedMessage
                id="XRayConfiguration.dropdownOptions.howItWorks.aiExampleDescription"
                defaultMessage="Examples of AI populating the dropdown field with values it
                found in your documents."
                description="Description of example showing how AI populates dropdown options."
              />
            </Text>
            <Text>
              <FormattedMessage
                id="XRayConfiguration.dropdownOptions.howItWorks.aiExampleText"
                defaultMessage="“Extract the SKU’s in the document”"
                description="Example text for how AI populates dropdown options."
              />
            </Text>
            <Image alt="hover-ai-example" src={aiExample} />
            <Text variant="body-medium">
              <FormattedMessage
                id="XRayConfiguration.dropdownOptions.howItWorks.customExampleDescription"
                defaultMessage="Examples of sorting data into options you define."
                description="Description of example showing how custom, close-ended dropdown options work."
              />
            </Text>
            <Text>
              <FormattedMessage
                id="XRayConfiguration.dropdownOptions.howItWorks.customExampleText"
                defaultMessage="“Is this a mutual or unilateral NDA?”"
                description="Example text for how custom, close-ended dropdown options work."
              />
            </Text>
            <Image alt="hover-user-example" src={userExample} />
          </Layout>
        ),
        label: intl.formatMessage({
          id: 'XRayConfiguration.dropdownOptions.howItWorks.label',
          defaultMessage: 'How it works',
          description:
            'Label for examples showing how the dropdown options configuration works.',
        }),
      }}
      input={CardSelect}
      inputProps={{
        options: configureDropdownOptions,
      }}
      label={intl.formatMessage({
        id: 'XRayConfiguration.dropdownOptions.formFieldLabel',
        defaultMessage: 'Configure Dropdown Options',
        description: 'Label for configuring dropdown options in Custom AI.',
      })}
      name="configure-dropdown-options"
      tooltip={readOnlyTooltip}
      value={isCloseEnded}
      onChange={(isCloseEnded) =>
        dispatch(slices.xRay.actions.setIsOpenEnded(!isCloseEnded))
      }
    />
  );
};
